/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  AlertTriangle,
  History,
  Settings,
  Calendar,
} from 'lucide-react';

export type AgentFocusTab = 'overview' | 'findings' | 'history' | 'schedule' | 'configure';

interface AgentFocusTabMenuProps {
  activeTab: AgentFocusTab;
  onTabChange: (tab: AgentFocusTab) => void;
  findingsCount?: number;
}

const tabs: { id: AgentFocusTab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'findings', label: 'Findings', icon: AlertTriangle },
  { id: 'history', label: 'Run History', icon: History },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'configure', label: 'Configure', icon: Settings },
];

export function AgentFocusTabMenu({ activeTab, onTabChange, findingsCount }: AgentFocusTabMenuProps) {
  return (
    <nav className="flex flex-col space-y-1 w-48">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
              isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{tab.label}</span>
            {tab.id === 'findings' && findingsCount !== undefined && findingsCount > 0 && (
              <span className={cn(
                'ml-auto px-2 py-0.5 text-xs font-semibold rounded-full',
                isActive
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-red-100 text-red-700'
              )}>
                {findingsCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
